import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { PinInput } from "@/components/PinInput";
import { AuthCard, AuthMessage, authInputCls, authButtonCls, authLabelCls } from "./components/AuthCard";

const MIN_LENGTH = 10;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [minutesLeft, setMinutesLeft] = useState(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [withPassword, setWithPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) { setValid(false); return undefined; }
    api.post("/auth/reset-password/validate", { token })
      .then((r) => { if (active) { setValid(Boolean(r.data.valid)); setMinutesLeft(r.data.minutes_remaining ?? null); } })
      .catch(() => active && setValid(false));
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => navigate("/admin/login", { replace: true, state: { message: done } }), 2500);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pin.length !== 7) { setError("PIN must be exactly 7 digits."); return; }
    if (pin !== confirmPin) { setError("PINs do not match."); return; }
    if (withPassword) {
      if (password !== confirm) { setError("Passwords do not match."); return; }
      if (password.length < MIN_LENGTH) { setError(`Password must be at least ${MIN_LENGTH} characters.`); return; }
    }
    setLoading(true);
    try {
      const payload = { token, pin, confirm_pin: confirmPin };
      if (withPassword) { payload.password = password; payload.confirm_password = confirm; }
      const r = await api.post("/auth/reset-password", payload);
      setDone(r.data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthCard testid="reset-password-card" title="Access restored">
        <div className="flex items-start gap-3 rounded-2xl bg-white border border-sand p-4" data-testid="reset-password-success">
          <CheckCircle2 className="w-6 h-6 shrink-0 text-[#15803D]" strokeWidth={2.2} />
          <p className="text-sm font-semibold text-navy">{done}</p>
        </div>
        <p className="mt-4 text-xs text-slate-500">Taking you back to the sign-in page…</p>
        <Link to="/admin/login" state={{ message: done }} data-testid="reset-go-to-login" className="mt-5 inline-block text-sm font-semibold text-navy underline decoration-gold/60 underline-offset-4">Go to Sign In now</Link>
      </AuthCard>
    );
  }

  if (valid === false) {
    return (
      <AuthCard testid="reset-password-card" title="Link expired">
        <AuthMessage testid="reset-password-invalid">This reset link is invalid, has expired, or was already used. Please request a new one.</AuthMessage>
        <Link to="/admin/forgot-password" data-testid="request-new-link" className={`${authButtonCls} mt-5 inline-flex items-center justify-center`}>REQUEST A NEW LINK</Link>
        <Link to="/admin/login" data-testid="back-to-login-link" className="mt-5 inline-block text-sm font-semibold text-navy hover:text-gold transition-colors">Back to Sign In</Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard testid="reset-password-card" title="Choose a new PIN" subtitle="Pick the 7-digit PIN you'll use to sign in. You can also set a new password below if you've forgotten it.">
      {valid === null ? (
        <p className="text-sm text-slate-500" data-testid="reset-password-checking">Checking your reset link…</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
          {minutesLeft != null && (
            <p className="text-xs font-semibold text-[#15803D]" data-testid="reset-link-minutes-remaining">
              This link is valid for about {minutesLeft} more minute{minutesLeft === 1 ? "" : "s"}.
            </p>
          )}
          <div>
            <label className={authLabelCls} htmlFor="rp-pin">New 7-digit PIN</label>
            <PinInput id="rp-pin" testid="reset-new-pin-input" className={authInputCls} value={pin} onChange={setPin} autoFocus />
          </div>
          <div>
            <label className={authLabelCls} htmlFor="rp-pin-confirm">Confirm new PIN</label>
            <PinInput id="rp-pin-confirm" testid="reset-confirm-pin-input" className={authInputCls} value={confirmPin} onChange={setConfirmPin} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" className="h-5 w-5 accent-[#C59B27]" checked={withPassword} onChange={(e) => setWithPassword(e.target.checked)} data-testid="reset-with-password-toggle" />
            <span className="text-sm font-semibold text-navy">Also set a new password</span>
          </label>
          {withPassword && (
            <>
              <div>
                <label className={authLabelCls} htmlFor="rp-new">New Password</label>
                <PasswordInput id="rp-new" testid="reset-new-password-input" className={authInputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={MIN_LENGTH} required={withPassword} />
              </div>
              <div>
                <label className={authLabelCls} htmlFor="rp-confirm">Confirm New Password</label>
                <PasswordInput id="rp-confirm" testid="reset-confirm-password-input" className={authInputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" minLength={MIN_LENGTH} required={withPassword} />
              </div>
            </>
          )}
          {error && <AuthMessage testid="reset-password-error">{error}</AuthMessage>}
          <button type="submit" disabled={loading} data-testid="reset-password-submit" className={authButtonCls}>
            {loading ? "UPDATING…" : withPassword ? "SAVE PIN & PASSWORD" : "SAVE NEW PIN"}
          </button>
        </form>
      )}
    </AuthCard>
  );
}
